import { useEffect, useState, useMemo } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  CheckCircle2, XCircle, Clock, ShieldCheck, ChevronRight, Info, Filter,
  History, Archive, Trash2, ArchiveRestore, Link as LinkIcon, UserMinus, UserCheck,
} from 'lucide-react';

const TABS = [
  { id: 'pending', label: 'Pending', icon: Clock },
  { id: 'approved', label: 'Approved', icon: CheckCircle2 },
  { id: 'rejected', label: 'Rejected', icon: XCircle },
  { id: 'restore-log', label: 'Restore log', icon: History },
];

const ACTION_LABELS = {
  modify: 'Modify',
  set_record_state: 'Set state',
  delete_permanent: 'Permanent delete',
};

function relTime(iso) {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  for (const [u, sec] of [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]]) {
    if (s >= sec) { const n = Math.floor(s / sec); return `${n} ${u}${n > 1 ? 's' : ''} ago`; }
  }
  return 'just now';
}

export default function ApprovalsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReview, setOpenReview] = useState(null); // { row, decision: 'approve'|'reject' }

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'restore-log') {
        const r = await api.get('/approvals/restore-log?days=30');
        setRows(r.data.rows || []);
      } else {
        const r = await api.get(`/approvals?status=${tab}`);
        setRows(r.data.rows || []);
      }
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  // Owner-only guard (double-belt with the backend 403).
  if (user && user.role !== 'owner') return <Navigate to="/" replace />;

  return (
    <AppShell breadcrumbs={[{ label: 'Approvals' }]}>
      <div className="p-6 max-w-[1120px] mx-auto fade-up" data-testid="approvals-page">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-heading text-[26px] font-semibold leading-tight">Approvals</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[720px]">
              Every bulk-modify or delete request from Accounts and Design Ops
              lands here. Approve or reject with one click — the requester is
              emailed either way.
            </p>
          </div>
        </div>

        <div className="rounded-md p-3 mb-4 flex items-start gap-2.5 bg-[hsl(var(--surface-1))] border" style={{ borderColor: 'hsl(var(--border))' }}>
          <Info size={14} className="mt-0.5 text-[hsl(var(--foreground-muted))]" />
          <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
            Content approvals (SOPs, templates, resources) live in the <a href="/knowledge" className="underline">Knowledge Hub</a>.
            This page only handles <strong>data-management</strong> requests across Team, Work Orders, Trips &amp; Visits, and Quotes.
          </div>
        </div>

        <div className="flex items-center gap-1 mb-4 flex-wrap" role="tablist">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab" aria-selected={active}
                onClick={() => setTab(t.id)}
                data-testid={`approvals-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-md border ${
                  active ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]'
                         : 'bg-[hsl(var(--surface-1))] hover:bg-[hsl(var(--surface-2))]'
                }`}
                style={!active ? { borderColor: 'hsl(var(--border))' } : {}}
              >
                <Icon size={12} />{t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-[13px] text-[hsl(var(--foreground-muted))] py-10 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          tab === 'restore-log' ? <RestoreLogEmpty /> : <EmptyState tab={tab} />
        ) : tab === 'restore-log' ? (
          <RestoreLogList rows={rows} />
        ) : (
          <div className="space-y-2" data-testid="approvals-list">
            {rows.map(r => (
              <ApprovalRow
                key={r.id} row={r} readOnly={tab !== 'pending'}
                onReview={(decision) => setOpenReview({ row: r, decision })}
              />
            ))}
          </div>
        )}
      </div>

      {openReview && (
        <ReviewDialog
          entry={openReview}
          onClose={() => setOpenReview(null)}
          onDone={() => { setOpenReview(null); load(); }}
        />
      )}
    </AppShell>
  );
}

function EmptyState({ tab }) {
  return (
    <div className="rounded-lg border p-10 text-center" style={{ borderColor: 'hsl(var(--border))' }}
         data-testid="approvals-empty">
      <ShieldCheck className="mx-auto mb-2 text-[hsl(var(--foreground-muted))]" size={22} />
      <div className="text-[13px] font-semibold">
        {tab === 'pending' ? 'Nothing waiting for your review.' :
         tab === 'approved' ? 'No approved requests yet.' : 'No rejections yet.'}
      </div>
      <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">
        Requests appear here when Accounts or Design Ops run a bulk action.
      </div>
    </div>
  );
}

function RestoreLogEmpty() {
  return (
    <div className="rounded-lg border p-10 text-center" style={{ borderColor: 'hsl(var(--border))' }}
         data-testid="restore-log-empty">
      <History className="mx-auto mb-2 text-[hsl(var(--foreground-muted))]" size={22} />
      <div className="text-[13px] font-semibold">No archive / restore activity in the last 30 days.</div>
      <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">
        Cascaded archives, deletes, restores, and WO attachments all show up here for audit.
      </div>
    </div>
  );
}

const RESTORE_ICON = {
  archive_cascade:   Archive,
  delete_cascade:    Trash2,
  unarchive_cascade: ArchiveRestore,
  unlink_cascade:    UserMinus,
  relink_cascade:    UserCheck,
  attach_work_order: LinkIcon,
};

const RESTORE_TINT = {
  archive_cascade:   { bg: 'hsl(48 96% 92%)',  fg: 'hsl(38 60% 30%)' },  // amber
  delete_cascade:    { bg: 'hsl(0 90% 94%)',   fg: 'hsl(0 60% 40%)' },   // red
  unarchive_cascade: { bg: 'hsl(142 70% 92%)', fg: 'hsl(142 50% 25%)' }, // green
  unlink_cascade:    { bg: 'hsl(30 80% 92%)',  fg: 'hsl(30 60% 30%)' },  // orange
  relink_cascade:    { bg: 'hsl(200 80% 92%)', fg: 'hsl(210 60% 30%)' }, // blue
  attach_work_order: { bg: 'hsl(260 60% 94%)', fg: 'hsl(260 50% 40%)' }, // purple
};

function RestoreLogList({ rows }) {
  // Group entries by day so the timeline reads naturally.
  const grouped = useMemo(() => {
    const by = {};
    for (const r of rows) {
      const day = (r.ts || '').slice(0, 10);
      (by[day] ||= []).push(r);
    }
    return Object.entries(by).sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="restore-log-list">
      <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] flex items-center gap-1.5">
        <Info size={11} />
        Showing the last 30 days · {rows.length} event{rows.length === 1 ? '' : 's'}
      </div>
      {grouped.map(([day, dayRows]) => (
        <div key={day} data-testid={`restore-log-day-${day}`}>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))] mb-1.5">
            {dayLabel(day)}
          </div>
          <div className="space-y-1.5">
            {dayRows.map(r => <RestoreLogRow key={r.id} row={r} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function RestoreLogRow({ row }) {
  const Icon = RESTORE_ICON[row.action] || History;
  const tint = RESTORE_TINT[row.action] || { bg: 'hsl(var(--surface-2))', fg: 'hsl(var(--foreground))' };
  const [expanded, setExpanded] = useState(false);
  const primary = row.targets?.[0];
  const extra = (row.targets?.length || 0) - 1;

  const counts = row.counts || {};
  const cascadeSummary = Object.entries(counts)
    .filter(([, v]) => Number(v) > 0)
    .map(([k, v]) => `${v} ${k.replace('_', ' ')}`)
    .join(' · ');

  return (
    <div className="rounded-lg border px-3 py-2 flex items-start gap-2.5 bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`restore-log-row-${row.id}`}>
      <div className="w-8 h-8 rounded-md grid place-items-center flex-none"
           style={{ background: tint.bg, color: tint.fg }}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-[12.5px]">
          <span className="chip text-[10.5px]" style={{ background: tint.bg, color: tint.fg }}
                data-testid={`restore-log-action-${row.id}`}>
            {row.action_label}
          </span>
          <span className="chip text-[10.5px]">{row.module_label}</span>
          <span className="font-medium truncate">{primary?.label || '—'}</span>
          {primary?.sublabel && (
            <span className="text-[11.5px] text-[hsl(var(--foreground-muted))] truncate">
              · {primary.sublabel}
            </span>
          )}
          {extra > 0 && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-[11px] underline text-[hsl(var(--foreground-muted))]"
              data-testid={`restore-log-toggle-${row.id}`}
            >
              {expanded ? 'hide' : `+${extra} more`}
            </button>
          )}
        </div>
        <div className="text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span>{row.actor_email || '(system)'}</span>
          <span>·</span>
          <span>{relTime(row.ts)}</span>
          {cascadeSummary && (
            <>
              <span>·</span>
              <span title="Cascaded row counts">{cascadeSummary}</span>
            </>
          )}
        </div>
        {expanded && extra > 0 && (
          <ul className="mt-1.5 text-[11.5px] text-[hsl(var(--foreground-muted))] space-y-0.5 pl-2 border-l"
              style={{ borderColor: 'hsl(var(--border))' }}>
            {row.targets.slice(1).map(t => (
              <li key={t.id}>
                <span className="font-medium text-[hsl(var(--foreground))]">{t.label}</span>
                {t.sublabel && <span> · {t.sublabel}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function dayLabel(iso) {
  if (!iso) return '';
  const today = new Date();
  const d = new Date(iso + 'T00:00:00');
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = Math.floor((t0 - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function ApprovalRow({ row, readOnly, onReview }) {
  const badge = ACTION_LABELS[row.action] || row.action;
  const payload = row.payload || {};
  const desc = row.action === 'modify'
    ? `Set “${payload.field}” to “${payload.value ?? '—'}”`
    : row.action === 'set_record_state'
      ? `Set record state to “${payload.record_state}”`
      : `Permanently delete ${row.target_ids?.length || 0} row(s)`;

  const decisionBadge = row.status === 'approved'
    ? <span className="chip text-[10.5px]" style={{ background: 'hsl(var(--success)/.14)', color: 'hsl(var(--success))' }}>Approved</span>
    : row.status === 'rejected'
      ? <span className="chip text-[10.5px]" style={{ background: 'hsl(var(--destructive)/.14)', color: 'hsl(var(--destructive))' }}>Rejected</span>
      : null;

  return (
    <div className="rounded-lg border px-4 py-3 flex items-start gap-3 bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`approval-row-${row.id}`}>
      <div className="w-9 h-9 rounded-md grid place-items-center flex-none bg-[hsl(var(--primary)/.10)] text-[hsl(var(--primary))] text-[13px] font-semibold">
        {(row.requested_by_name || row.requested_by_email || '?').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-[13px]">
          <span className="font-semibold truncate">{row.requested_by_name || row.requested_by_email}</span>
          <span className="chip text-[10.5px]" style={{ background: 'hsl(var(--primary)/.10)', color: 'hsl(var(--primary))' }}>{row.module_label}</span>
          <span className="chip text-[10.5px]">{badge}</span>
          {decisionBadge}
        </div>
        <div className="text-[12.5px] mt-1">{desc}</div>
        <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5">
          {(row.target_summaries || []).slice(0, 3).map(t => t.label).join(' · ')}
          {(row.target_summaries || []).length > 3 && ` · +${row.target_summaries.length - 3} more`}
          <span className="mx-1.5">·</span>{relTime(row.requested_at)}
        </div>
        {row.review_note && (
          <div className="text-[11.5px] mt-1 text-[hsl(var(--foreground-muted))] italic">
            Reviewer note: “{row.review_note}”
          </div>
        )}
      </div>
      {!readOnly && (
        <div className="flex flex-col gap-1.5 flex-none">
          <Button size="sm" onClick={() => onReview('approve')}
                  data-testid={`approval-approve-${row.id}`}>
            <CheckCircle2 size={12} className="mr-1" />Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReview('reject')}
                  data-testid={`approval-reject-${row.id}`}>
            <XCircle size={12} className="mr-1" />Reject
          </Button>
        </div>
      )}
    </div>
  );
}

function ReviewDialog({ entry, onClose, onDone }) {
  const { row, decision } = entry;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/approvals/${row.id}/${decision}`, { note: note || null });
      toast.success(`Request ${decision === 'approve' ? 'approved' : 'rejected'}. The requester was notified.`);
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[440px]" data-testid={`approval-review-dialog-${decision}`}>
        <DialogHeader>
          <DialogTitle>{decision === 'approve' ? 'Approve request' : 'Reject request'}</DialogTitle>
          <DialogDescription>
            {decision === 'approve'
              ? 'The action will be applied immediately. The requester gets an email.'
              : 'No changes will be made. The requester gets an email with your note.'}
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))]">Note (optional)</label>
          <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Explain the decision…"
                 data-testid="approval-review-note" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={decision === 'approve' ? 'default' : 'destructive'}
                  onClick={submit} disabled={busy}
                  data-testid="approval-review-submit">
            {busy ? 'Working…' : (decision === 'approve' ? 'Approve now' : 'Reject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
